import React, { useState, useEffect, useRef } from 'react'
import { Modal, Input, InputNumber, List } from 'antd'
import axios from 'axios'
import { toast } from 'react-toastify'
import { useAuth } from '../../../../context/auth'

const BoxAddItem = ({
  id,
  rackno,
  boxno,
  isModalVisible,
  hideModal,
  onItemAdded,
}) => {
  const [auth] = useAuth()
  const userId = auth?.user?._id
  const [searchTerm, setSearchTerm] = useState('')
  const [quantity, setQuantity] = useState(1)
  const [remark, setRemark] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [selectedItem, setSelectedItem] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const searchIdRef = useRef(0)

  useEffect(() => {
    const fetchItems = async () => {
      if (searchTerm.length < 3) {
        setSearchResults([])
        return
      }

      // Only the newest keystroke may write results - the debounce cleared
      // the timer but not an already in-flight request.
      const searchId = ++searchIdRef.current
      setIsSearching(true)
      try {
        const { data } = await axios.get(
          '/api/v1/admin/stock-check-store/add-item-box/search',
          { params: { query: searchTerm }, timeout: 20000 }
        )
        if (searchId !== searchIdRef.current) return
        setSearchResults(Array.isArray(data) ? data : [])
      } catch (error) {
        if (searchId !== searchIdRef.current) return
        console.error('Search error:', error)
        setSearchResults([])
        toast.error(error.response?.data?.message || 'Item search failed.')
      } finally {
        if (searchId === searchIdRef.current) setIsSearching(false)
      }
    }

    const delayDebounce = setTimeout(() => {
      if (!selectedItem) fetchItems()
    }, 300)

    return () => clearTimeout(delayDebounce)
  }, [searchTerm, selectedItem])

  const handleItemSelect = (item) => {
    setSelectedItem(item)
    setSearchTerm(`${item.itemCode} - ${item.description}`)
    setSearchResults([])
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      const { data } = await axios.post(
        '/api/v1/admin/stock-check-store/add-item-box',
        {
          id,
          rackno,
          boxno,
          itemId: selectedItem._id,
          quantity,
          userId,
          remark,
        }
      )

      const item = {
        ...data.item,
        lastUptByCode: auth?.user?.code,
      }

      if (onItemAdded) onItemAdded(item)

      hideModal()
      setSearchTerm('')
      setSelectedItem(null)
      setQuantity(1)
      setRemark('')
      toast.success('Item added')
    } catch (error) {
      // Silent before: the modal stayed open with no explanation.
      console.error('Submit error:', error)
      toast.error(
        error.response?.data?.message || 'Failed to add the item. Not saved.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      title='Add Item to Box'
      open={isModalVisible}
      onCancel={() => {
        hideModal()
        setSelectedItem(null)
        setSearchTerm('')
        setQuantity(1)
        setRemark('')
      }}
      footer={null}
      width='90%'
      style={{ maxWidth: 640 }}
    >
      <div className='mb-3'>
        <label className='form-label fw-bold'>Search Item name or code</label>
        <div style={{ display: 'flex' }}>
          <Input
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setSelectedItem(null)
            }}
            disabled={!!selectedItem}
            style={{
              flex: 1,
              backgroundColor: '#fff',
              color: selectedItem ? '#000' : 'inherit',
              cursor: selectedItem ? 'not-allowed' : 'text',
            }}
          />

          <button
            type='button'
            className='btn btn-secondary btn-sm'
            onClick={() => {
              setSearchTerm('')
              setSelectedItem(null)
              setSearchResults([])
            }}
            disabled={!searchTerm}
          >
            Clear
          </button>
        </div>

        {!selectedItem && searchResults.length > 0 && (
          <List
            bordered
            size='small'
            dataSource={searchResults}
            renderItem={(item) => (
              <List.Item
                onClick={() => handleItemSelect(item)}
                style={{ cursor: 'pointer' }}
              >
                {item.itemCode} - {item.description}
              </List.Item>
            )}
            className='mt-2'
          />
        )}

        {/* Without these, an empty result and a search still running looked
            identical - nothing rendered at all. */}
        {!selectedItem && isSearching && (
          <div className='text-muted small mt-2'>Searching…</div>
        )}
        {!selectedItem &&
          !isSearching &&
          searchTerm.length >= 3 &&
          searchResults.length === 0 && (
            <div className='text-muted small mt-2'>No matching item found.</div>
          )}
        {!selectedItem && searchTerm.length > 0 && searchTerm.length < 3 && (
          <div className='text-muted small mt-2'>
            Type at least 3 characters.
          </div>
        )}
      </div>

      <div className='mb-3'>
        <label className='form-label fw-bold'>Quantity</label>
        <InputNumber
          min={1}
          value={quantity}
          onChange={(value) => setQuantity(value)}
          // Select the existing value so typing replaces it instead of
          // appending to the default 1.
          onFocus={(e) => e.target.select()}
          style={{ width: '100%' }}
        />
      </div>

      <div className='mb-3'>
        <label className='form-label fw-bold'>Remark</label>
        <Input value={remark} onChange={(e) => setRemark(e.target.value)} />
      </div>

      <div className='d-flex justify-content-end'>
        <button
          type='button'
          className='btn btn-success me-3'
          onClick={handleSubmit}
          disabled={!selectedItem || isSubmitting || !(Number(quantity) > 0)}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>

        <button
          type='button'
          className='btn btn-secondary'
          onClick={() => {
            hideModal()
            setSearchTerm('')
            setQuantity(1)
            setSelectedItem(null)
            setRemark('')
          }}
        >
          Cancel
        </button>
      </div>
    </Modal>
  )
}

export default BoxAddItem
